import { supabase } from '@/shared/lib/supabase';
import { isProjectStatus } from '@/shared/constants/statuses';
import type { IsoDateTime, ProjectRowView } from '@/shared/types/domain';

/**
 * Única camada de `home` que conhece o supabase-js (architecture.md §5.1).
 *
 * **Nada aqui filtra por papel, e isso é o assunto desta feature.** Quem recorta
 * é a RLS: `projects_with_context` é uma view `security_invoker`, então
 * `projects_select` se aplica inteira — inclusive a estreitagem que esconde
 * rascunho de ministério onde a pessoa não tem vínculo direto. O líder recebe o
 * próprio ministério; o coordenador, a coordenação; o overseer, a filial.
 *
 * O que a Início acrescenta não é alcance, é MOLDURA: cada bloco diz de quem é
 * o número que mostra. É a lição da US-11, que foi retirada da tela de
 * coordenação por nomear uma coordenação e entregar o recorte de quem olhava.
 *
 * ⚠️ **A densidade das próximas semanas NÃO passa por aqui** — ela é
 * `fetchAgendaProjects`, do barrel de `features/projects`, e alcança a filial
 * inteira de propósito (ADR-015). São dois escopos na mesma tela, legítimos, e
 * é por isso que cada bloco carrega o seu por extenso.
 */

/**
 * O que a janela de volume devolve: **o `ProjectRowView` de sempre**, com a
 * garantia de que `starts_at` existe.
 *
 * ⚠️ **Reusa o tipo compartilhado em vez de um mais estreito, e a primeira
 * versão errou nisso.** Ela definia sete campos "por disciplina de projeção",
 * emprestando o argumento da `agenda_projects` — que é uma RPC `SECURITY
 * DEFINER` servindo a filial inteira, onde a lista de colunas É a decisão de
 * privacidade e um teste pgTAP a trava. Aqui não: `projects_with_context` é
 * view `security_invoker`, a RLS já recortou a linha, e toda coluna que chega é
 * coluna que aquela pessoa pode ler. Estreitar não protegia nada — só obrigava
 * o `ProjectTable`, que é **a única forma de listar projeto no painel**, a
 * receber um tipo que não existe em lugar nenhum.
 *
 * O `starts_at` não-nulo é o que a estreitagem legítima acrescenta: a consulta
 * filtra por data, então toda linha que chega tem uma — e sem isso cada
 * agregação daqui carregaria um `?? ''` em cima de um campo que nunca é nulo.
 */
export type HomeProject = ProjectRowView & { starts_at: IsoDateTime };

const VOLUME_COLUMNS =
  'id, title, status, starts_at, ends_at, ministry_id, ministry_name, coordination_id, coordination_name, responsible_name, submitted_at, decided_at, space_names';

function narrow(row: {
  id: string | null;
  title: string | null;
  status: string | null;
  starts_at: string | null;
  ends_at: string | null;
  ministry_id: string | null;
  ministry_name: string | null;
  coordination_id: string | null;
  coordination_name: string | null;
  responsible_name: string | null;
  submitted_at: string | null;
  decided_at: string | null;
  space_names: string[] | null;
}): HomeProject[] {
  // Linha sem identidade, situação, cadeia organizacional ou data é DESCARTADA
  // e não remendada — mesmo critério do `narrow()` de `features/projects`. Aqui
  // o `starts_at` entra na espinha dorsal porque todo consumidor desta função
  // agrupa por data: sem ela, a linha não tem coluna onde cair.
  if (
    !row.id ||
    !row.status ||
    !isProjectStatus(row.status) ||
    !row.starts_at ||
    !row.ministry_id ||
    !row.ministry_name ||
    !row.coordination_id ||
    !row.coordination_name
  ) {
    return [];
  }
  return [
    {
      id: row.id,
      title: row.title,
      status: row.status,
      starts_at: row.starts_at,
      ends_at: row.ends_at,
      ministry_id: row.ministry_id,
      ministry_name: row.ministry_name,
      coordination_id: row.coordination_id,
      coordination_name: row.coordination_name,
      responsible_name: row.responsible_name,
      submitted_at: row.submitted_at,
      decided_at: row.decided_at,
      space_names: row.space_names ?? [],
    },
  ];
}

/**
 * Os projetos da janela de volume — seis meses atrás a três à frente.
 *
 * Serve TRÊS blocos numa consulta só: volume por mês, "o que vem por aí" e
 * "onde está a atividade". Três consultas devolveriam as mesmas linhas com três
 * caches para invalidar e três chances de discordarem entre si na mesma tela.
 *
 * ⚠️ **A janela é limitada, e isso é requisito, não estilo.** O `max_rows` do
 * PostgREST é 1000 e ele trunca **em silêncio** — um agregado truncado não
 * quebra, ele mente. Dez meses dão ~400 linhas no pior caso (admin, ~500
 * projetos/ano). Se a igreja crescer a ponto de encostar no teto, a saída é uma
 * view agregada por mês no banco, **não** uma janela maior: o problema não é o
 * tamanho da janela, é contar no cliente o que já passou do tamanho de contar
 * no cliente.
 *
 * `draft` fica de fora, e por duas razões que se somam: ele não tem data
 * confiável (nasce vazio, ADR-005) e a RLS já o esconde de quase todo mundo —
 * mantê-lo faria a mesma coluna do gráfico ter altura diferente conforme QUEM
 * olha, dentro do mesmo ministério. O rascunho tem lugar próprio na tela: o
 * atalho para `/rascunhos`.
 */
export async function fetchHomeVolume(from: string, to: string): Promise<HomeProject[]> {
  const { data, error } = await supabase
    .from('projects_with_context')
    .select(VOLUME_COLUMNS)
    .neq('status', 'draft')
    .gte('starts_at', from)
    .lt('starts_at', to)
    .order('starts_at', { ascending: true });
  if (error) throw error;

  return (data ?? []).flatMap(narrow);
}

/**
 * Uma decisão tomada na janela recente: quanto tempo o projeto esperou.
 *
 * Só os dois carimbos, e nem sequer o id — o bloco de saúde da esteira mostra
 * mediana e contagem, nunca uma lista. Trazer título e ministério para calcular
 * uma mediana seria carregar dado que a tela não exibe.
 */
export interface DecisionRecord {
  submitted_at: IsoDateTime;
  decided_at: IsoDateTime;
}

/**
 * As decisões dos últimos N dias, para a mediana até decisão (PRD §6.3).
 *
 * Recortado pela mesma RLS de tudo: para o overseer é a filial dele, que é
 * exatamente o conjunto sobre o qual ele responde. O bloco que consome isto só
 * aparece para quem tem `reviewProject`, mas **a estreitagem que vale é a do
 * banco** — a `ability` esconde o bloco, não protege o dado.
 */
export async function fetchDecisions(since: string): Promise<DecisionRecord[]> {
  const { data, error } = await supabase
    .from('projects_with_context')
    .select('submitted_at, decided_at')
    .not('submitted_at', 'is', null)
    .gte('decided_at', since);
  if (error) throw error;

  return (data ?? []).flatMap((row) =>
    row.submitted_at && row.decided_at
      ? [{ submitted_at: row.submitted_at, decided_at: row.decided_at }]
      : [],
  );
}
